import React from "react";
import { useFormContext } from "react-hook-form";

import { HookFormRadioButton } from "components";
import { useBackendPlatform } from "hooks";
import {
	TerminalFileBuildOptionTypes,
	TerminalGatewayTypes,
} from "types/enums";

import OtherFileBuild from "./otherFileBuild";
import TsysStageOnlyVar from "./tsysStageOnlyVar";
import TsysSummit from "./tsysSummit";
import TsysTransit from "./tsysTransit";

import styles from "../terminalSolution.module.scss";

type FileBuildInfoProps = {
	sectionName: string;
	isLoading?: boolean;
	onSubmitStageOnlyVar?: Function;
	setIsCreationStageOnlyVar?: any;
	refetchTerminalData?: Function | undefined;
	fileBuild?: any;
};

const FileBuildInfo = ({
	sectionName,
	isLoading = false,
	onSubmitStageOnlyVar,
	setIsCreationStageOnlyVar,
	refetchTerminalData,
	fileBuild,
}: FileBuildInfoProps) => {
	const { register, watch } = useFormContext();

	const option = watch(`${sectionName}.fileBuildOption`);
	const backEndPlatform = watch(`backEndPlatform`);
	const { isBackEndPlatformTsys } = useBackendPlatform(backEndPlatform);

	const isGateway = watch("isGateway");
	const isPointOfSale = watch("isPointOfSale");
	const gatewayName = watch("gateway.gatewayName.name");
	const isGatewayArise = gatewayName === TerminalGatewayTypes.ARISE.name;

	const isTidVNumberFromApi = fileBuild?.isIntegratedWithTsys;

	const showTsysStageOnlyVar =
		isBackEndPlatformTsys &&
		((isTidVNumberFromApi &&
			fileBuild?.fileBuildOption ===
				TerminalFileBuildOptionTypes.TsysStageOnlyVar.name) ||
			!isTidVNumberFromApi);
	const showTsysTransit =
		isBackEndPlatformTsys &&
		(isGateway || isPointOfSale) &&
		((isTidVNumberFromApi &&
			fileBuild?.fileBuildOption ===
				TerminalFileBuildOptionTypes.TsysTransit.name) ||
			!isTidVNumberFromApi);
	const showTsysSummit = isBackEndPlatformTsys && !isTidVNumberFromApi;
	const showOther = !isTidVNumberFromApi;

	return (
		<div data-autotest="fileBuildInfoSection">
			{showTsysStageOnlyVar && (
				<div className={styles.section}>
					<HookFormRadioButton
						id="tsysStageOnlyVar"
						register={register}
						name={`${sectionName}.fileBuildOption`}
						value={
							TerminalFileBuildOptionTypes.TsysStageOnlyVar.name
						}
						label="TSYS Stage only VAR"
						blurLoading={isLoading}
						disabled={isTidVNumberFromApi || isGatewayArise}
					/>

					{option ===
						TerminalFileBuildOptionTypes.TsysStageOnlyVar.name && (
						<TsysStageOnlyVar
							isLoading={isLoading}
							sectionName={`${sectionName}.tsysTerminalSettings`}
							onSubmitStageOnlyVar={onSubmitStageOnlyVar}
							setIsCreationStageOnlyVar={
								setIsCreationStageOnlyVar
							}
							refetchTerminalData={refetchTerminalData}
							isTidVNumberFromApi={isTidVNumberFromApi}
						/>
					)}
					<hr className={styles.divider} />
				</div>
			)}

			{showTsysSummit && (
				<div className={styles.section}>
					<HookFormRadioButton
						id="tsysSummit"
						register={register}
						name={`${sectionName}.fileBuildOption`}
						value={TerminalFileBuildOptionTypes.TsysSummit.name}
						label="TSYS Summit"
						blurLoading={isLoading}
						disabled={isGatewayArise}
					/>
					{option ===
						TerminalFileBuildOptionTypes.TsysSummit.name && (
						<TsysSummit
							isLoading={isLoading}
							sectionName={`${sectionName}.tsysTerminalSettings`}
						/>
					)}
					<hr className={styles.divider} />
				</div>
			)}

			{showTsysTransit && (
				<div className={styles.section}>
					<HookFormRadioButton
						id="tsysTransit"
						register={register}
						name={`${sectionName}.fileBuildOption`}
						value={TerminalFileBuildOptionTypes.TsysTransit.name}
						label="TSYS Transit"
						blurLoading={isLoading}
						disabled={isTidVNumberFromApi}
					/>
					{option ===
						TerminalFileBuildOptionTypes.TsysTransit.name && (
						<TsysTransit
							isLoading={isLoading}
							sectionName={`${sectionName}.tsysTerminalSettings`}
							onSubmitStageOnlyVar={onSubmitStageOnlyVar}
							setIsCreationStageOnlyVar={
								setIsCreationStageOnlyVar
							}
							refetchTerminalData={refetchTerminalData}
							isTidVNumberFromApi={isTidVNumberFromApi}
						/>
					)}
					<hr className={styles.divider} />
				</div>
			)}

			{showOther && (
				<div
					className={styles.section}
					data-autotest="otherFileBuildColumn"
				>
					<HookFormRadioButton
						id="otherFileBuild"
						register={register}
						name={`${sectionName}.fileBuildOption`}
						value={TerminalFileBuildOptionTypes.Other.name}
						label="Other"
						blurLoading={isLoading}
						disabled={isGatewayArise}
					/>
					{option === TerminalFileBuildOptionTypes.Other.name && (
						<OtherFileBuild isLoading={isLoading} />
					)}
				</div>
			)}
		</div>
	);
};

export default FileBuildInfo;
